/*
 * Close versions: response shaping and error routing. Pure; the components render what these
 * return.
 */
import { EM_DASH, api_error, fmt, fmt_signed, is_permission_error, num } from '../format';
import { with_ist } from '../shared/ist';
import { MODE_REOPENED_NOW } from './params';

// close_versions_enums.json components, in drill-down row order; the payable closes the table.
export const COMPONENTS = [
  { key: 'one_on_one', label: '1-on-1 sessions' },
  { key: 'group_class', label: 'Group classes' },
  { key: 'trial', label: 'Trial incentives' },
  { key: 'incentive', label: 'Incentives' },
  { key: 'additional', label: 'Additional payments' },
  { key: 'penalty', label: 'Penalties' },
  { key: 'waived_penalty', label: 'Waived penalties' },
  { key: 'encashment', label: 'Free Pass encashment' },
  { key: 'gross', label: 'Gross' },
  { key: 'gst', label: 'GST' },
  { key: 'tds', label: 'TDS' },
  { key: 'net', label: 'Net' },
  { key: 'carry_forward', label: 'Carry-forward' }
];
export const PAYABLE = { key: 'payable_amount', label: 'Payable' };
const LINES = [...COMPONENTS, PAYABLE];

export const SOURCE_LABELS = {
  live: 'Live now',
  snapshot: 'Saved at close',
  rebuilt_from_history: 'Rebuilt from history',
  net_only: 'Net only'
};
export const NOT_AVAILABLE = 'Not available';

const str = (v) => (typeof v === 'string' ? v : '');
const actor_name = (a) => (a && typeof a === 'object' && str(a.name)) || '';

// ---- summary ----

export const summary_view = (data) => {
  const d = data || {};
  const run = d.active_run && typeof d.active_run === 'object' ? d.active_run : null;
  return {
    reopened_now: num(d.reopened_now),
    reopened_pending_recompute: num(d.reopened_pending_recompute),
    closed_teacher_months: num(d.closed_teacher_months),
    changed: num(d.changed),
    multi_version: num(d.multi_version),
    default_mode: str(d.default_mode),
    active_run: run ? { kind: str(run.kind), headline: str(run.headline) } : null
  };
};

// The shell's tab badge: teacher-months reopened now (hidden at 0).
export const close_versions_badge = (data) => num(data && data.reopened_now);

export const run_banner_text = (run, month_label) => {
  if (!run) return '';
  const what = run.kind === 'REOPEN' ? 'A reopen run' : 'A close run';
  return `${what} is in progress for ${month_label}.${run.headline ? ` ${run.headline}` : ''}`;
};

// ---- list rows ----

export const status_view = (row) => {
  if (row.lifecycle === 'CLOSED') return { label: 'Closed', tone: 'green' };
  if (num(row.reopen_count) > 0) return { label: 'Reopened', tone: 'orange' };
  return { label: 'Open', tone: 'gray' };
};

// "v2 · ₹18,450.25"; a side that is not a plain snapshot names its source.
const side_view = (ref) => {
  if (!ref) return null;
  const source = str(ref.source);
  return {
    text: `${str(ref.label)} · ${fmt(ref.payable_amount, 2)}`,
    label: str(ref.label),
    amount_text: fmt(ref.payable_amount, 2),
    source_note:
      source === 'rebuilt_from_history' || source === 'net_only' ? SOURCE_LABELS[source] : ''
  };
};

/*
 * changed is true when any component or the payable moved, so a payable-equal row can still be
 * changed ("No change to payable"). A null diff has no baseline to compare with.
 */
export const payable_diff_view = (payable_diff, changed, mode) => {
  if (payable_diff === null || payable_diff === undefined) {
    return {
      text: mode === MODE_REOPENED_NOW ? 'No close recorded' : 'First close',
      tone: 'muted'
    };
  }
  const n = num(payable_diff);
  if (n > 0) return { text: fmt_signed(n, 2), tone: 'pos' };
  if (n < 0) return { text: fmt_signed(n, 2), tone: 'neg' };
  return changed
    ? { text: 'No change to payable', tone: 'warn' }
    : { text: 'No change', tone: 'muted' };
};

export const pending_text = (row) => {
  if (str(row.compute_error)) return `Last recalculation failed: ${row.compute_error}`;
  const at = row.current && str(row.current.at_ist);
  return `Not recalculated since the reopen${at ? ` (last computed ${with_ist(at)})` : ''}`;
};

export const row_view = (row, mode) => {
  const r = row || {};
  const reopen = r.reopen && typeof r.reopen === 'object' ? r.reopen : null;
  const by =
    mode === MODE_REOPENED_NOW
      ? actor_name(reopen && reopen.by)
      : actor_name(r.current && r.current.by);
  return {
    key: str(r.teacher_uuid),
    teacher_uuid: str(r.teacher_uuid),
    teacher_name: str(r.teacher_name) || EM_DASH,
    teacher_slug: str(r.teacher_slug),
    status: status_view(r),
    version_count: num(r.version_count),
    before: side_view(r.baseline),
    after: side_view(r.current),
    diff: payable_diff_view(r.payable_diff, r.changed === true, mode),
    changed_at: with_ist(str(r.changed_at_ist)) || EM_DASH,
    changed_by: by,
    reopen_reason: reopen ? str(reopen.reason) : '',
    pending_recompute: mode === MODE_REOPENED_NOW && r.pending_recompute === true,
    pending_text: pending_text(r),
    // A failed recalculation is an error Ops must see, not a muted note.
    pending_failed: mode === MODE_REOPENED_NOW && !!str(r.compute_error)
  };
};

export const list_view = (data, mode) => {
  const d = data || {};
  return {
    rows: (Array.isArray(d.rows) ? d.rows : []).map((row) => row_view(row, mode)),
    total_count: num(d.total_count),
    filtered_count: num(d.filtered_count)
  };
};

// ---- the teacher-month dialog ----

const money_text = (key, v) => {
  if (v === null || v === undefined) return NOT_AVAILABLE;
  return fmt(v, 2);
};

const change_text = (key, diff) => {
  if (diff === null || diff === undefined) return NOT_AVAILABLE;
  if (num(diff) === 0) return 'No change';
  return fmt_signed(diff, 2);
};

// One comparison's per-line changes; the payable comes from payable_diff.
const comparison_lines = (cmp) => {
  const by_key = {};
  (Array.isArray(cmp.components) ? cmp.components : []).forEach((c) => {
    by_key[c.key] = c;
  });
  const out = {};
  COMPONENTS.forEach(({ key }) => {
    const c = by_key[key] || {};
    out[key] = { text: change_text(key, c.diff), changed: c.changed === true, diff: c.diff };
  });
  out[PAYABLE.key] = {
    text: change_text(PAYABLE.key, cmp.payable_diff),
    changed: num(cmp.payable_diff) !== 0,
    diff: cmp.payable_diff
  };
  return out;
};

const version_meta = (v) => {
  const lines = [];
  const closer = actor_name(v.by);
  lines.push(`Closed ${with_ist(str(v.at_ist)) || EM_DASH}${closer ? ` by ${closer}` : ''}`);
  if (str(v.reason)) lines.push(`Reason: ${v.reason}`);
  if (v.close_action === 'close_with_open_issues') {
    lines.push(
      `Closed with open issues${str(v.close_override_reason) ? `: ${v.close_override_reason}` : ''}`
    );
  }
  if (v.reopen && typeof v.reopen === 'object') {
    const who = actor_name(v.reopen.by);
    lines.push(
      `Reopened ${with_ist(str(v.reopen.at_ist))}${who ? ` by ${who}` : ''}${str(v.reopen.reason) ? `: ${v.reopen.reason}` : ''}`
    );
  }
  return lines;
};

const live_meta = (live) => {
  const lines = [
    str(live.computed_at_ist)
      ? `Last computed ${with_ist(live.computed_at_ist)}`
      : 'Not computed yet'
  ];
  if (str(live.compute_error)) lines.push(`Last recalculation failed: ${live.compute_error}`);
  else if (live.pending_recompute) lines.push('Not recalculated since the reopen');
  return lines;
};

const column = (key, label, source, meta, figures, cmp) => ({
  key,
  label,
  source,
  source_label: SOURCE_LABELS[source] || source,
  meta,
  // raw figures for the CSV (null/absent = not available).
  figures: figures && typeof figures === 'object' ? figures : {},
  values: LINES.reduce((acc, { key: k }) => {
    acc[k] = money_text(k, figures ? figures[k] : null);
    return acc;
  }, {}),
  // The change against the column before it; null for the first version.
  change: cmp
    ? {
        baseline_label: str(cmp.baseline_label),
        changed: cmp.changed === true,
        lines: comparison_lines(cmp)
      }
    : null
});

// Lines whose rise lowers the payable: their delta colour is inverted.
const REDUCES_PAYABLE = new Set(['penalty', 'tds', 'carry_forward']);

// A delta's colour by its effect on payable: green when it pays more, red when less.
export const diff_tone = (diff, key) => {
  const n = num(diff) * (REDUCES_PAYABLE.has(key) ? -1 : 1);
  if (n > 0) return 'pos';
  if (n < 0) return 'neg';
  return '';
};

// The lines of a change that moved, for the "What changed" summary (net-only sides have none).
export const changed_lines = (change) =>
  change
    ? LINES.filter((l) => change.lines[l.key].changed).map((l) => ({
        key: l.key,
        label: l.label,
        text: change.lines[l.key].text,
        tone: diff_tone(change.lines[l.key].diff, l.key)
      }))
    : [];

export const teacher_month_view = (data) => {
  const d = data || {};
  const versions = Array.isArray(d.versions) ? d.versions : [];
  const columns = versions.map((v) =>
    column(
      str(v.label),
      str(v.label),
      str(v.source),
      version_meta(v),
      v.figures,
      v.diff_vs_previous
    )
  );
  if (d.live && typeof d.live === 'object') {
    const l = d.live;
    columns.push(
      column(
        'live',
        str(l.label) || SOURCE_LABELS.live,
        'live',
        live_meta(l),
        l.figures,
        l.diff_vs_previous
      )
    );
  }
  return {
    teacher_name: (d.teacher && str(d.teacher.name)) || '',
    lifecycle: str(d.lifecycle),
    reopen_count: num(d.reopen_count),
    lines: LINES,
    columns,
    // Newest first: the pair picker opens on the latest change.
    pairs: columns
      .map((c, i) => {
        if (!c.change || i === 0) return null;
        const named = columns.find((b) => b.label === c.change.baseline_label);
        return { id: c.key, before: named || columns[i - 1], after: c };
      })
      .filter(Boolean)
      .reverse()
  };
};

// ---- errors ----

// FORBIDDEN goes to the shell's no-permission page; the rest render in the tab with the server's sentence.
export const cv_error_view = (e, fallback) => {
  const { code, message } = api_error(e);
  if (is_permission_error(e) || code === 'FORBIDDEN') return { kind: 'permission', code };
  return { kind: 'inline', code, text: message || fallback };
};

// ---- empty states ----

/* What an empty page says, or null when there are rows. */
export const empty_state = ({ mode, list, filters_on, only_multi, month_label }) => {
  if (list.rows.length) return null;
  if (list.total_count === 0) {
    return mode === MODE_REOPENED_NOW
      ? { text: `Nothing is reopened in ${month_label}.`, action: 'show_close_versions' }
      : { text: `No teacher-month in ${month_label} has been closed yet.`, action: '' };
  }
  if (list.filtered_count === 0 && only_multi && !filters_on) {
    return {
      text: `No teacher-month in ${month_label} has been closed more than once.`,
      action: 'show_first_closes'
    };
  }
  if (list.filtered_count === 0) {
    return { text: 'No teacher-month matches these filters.', action: 'clear_filters' };
  }
  // Rows exist but not on this page (the list shrank under it): the tab steps back a page.
  return { text: '', action: 'page_back' };
};
